'use client';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/lib/auth-context';
import { VERTICALS, SERVICE_GENDERS } from '@slikerr/shared';
import { Button, Input, Badge, Card } from '@/components/ds';

/** Roles that can create/edit/archive services and manage categories. */
const EDIT_ROLES = ['manager', 'business_owner'] as const;

/** Blank state for the service create/edit form. categoryId starts empty — the
 * owner must explicitly pick a category, there is no implicit "first category"
 * default (a flat "New service" button has no section context to inherit from). */
const EMPTY_FORM = {
  name: '',
  categoryId: '',
  vertical: VERTICALS[0] as string,
  gender: 'unisex' as string,
  durationMinutes: 30,
  bufferBeforeMinutes: 0,
  bufferAfterMinutes: 0,
  price: '',
  currency: 'ILS',
};

type FormState = typeof EMPTY_FORM;

type ServiceItem = {
  id: string;
  categoryId: string;
  name: string;
  vertical: string | null;
  gender: string;
  durationMinutes: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  price: string | null;
  currency: string;
  isActive: boolean;
};

/** Stable identity for a category with no services — an inline `[]` would be a
 *  new array every render, which is the thing the grouping below exists to avoid. */
const EMPTY_SERVICES: ServiceItem[] = [];

/** Business-defined service grouping (distinct from `vertical`, the fixed
 * platform taxonomy). Owner-named, means nothing outside its own business. */
type CategoryItem = {
  id: string;
  name: string;
  sortOrder: number;
  serviceCount: number;
};

export default function ServicesPage() {
  const { t } = useTranslation();
  const auth = useAuth();

  const canEdit = EDIT_ROLES.includes(
    (auth.activeBusiness?.role ?? '') as (typeof EDIT_ROLES)[number],
  );

  // Include archived so the list can show archived rows with an Unarchive button.
  const listQuery = trpc.service.list.useQuery({ includeArchived: true }, { retry: 1 });
  const categoryListQuery = trpc.category.list.useQuery(undefined, { retry: 1 });
  const utils = trpc.useUtils();

  // A service mutation can change which category a row counts toward; a
  // category mutation can change names/order/existence — either one can make
  // the other query stale, so both invalidate together.
  const invalidateList = () => {
    void utils.service.list.invalidate();
    void utils.category.list.invalidate();
  };

  const createMutation = trpc.service.create.useMutation({ onSuccess: invalidateList });
  const updateMutation = trpc.service.update.useMutation({ onSuccess: invalidateList });
  const setActiveMutation = trpc.service.setActive.useMutation({ onSuccess: invalidateList });

  const categoryCreateMutation = trpc.category.create.useMutation({ onSuccess: invalidateList });
  const categoryUpdateMutation = trpc.category.update.useMutation({ onSuccess: invalidateList });
  const categoryDeleteMutation = trpc.category.delete.useMutation();
  const categoryReorderMutation = trpc.category.reorder.useMutation({ onSuccess: invalidateList });

  // Form visibility: null = hidden; 'create' = new form; string (id) = edit form.
  const [formMode, setFormMode] = useState<null | 'create' | string>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [nameError, setNameError] = useState<string | null>(null);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [durationError, setDurationError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Category name form: same null/'create'/id-string shape as the service form.
  const [categoryFormMode, setCategoryFormMode] = useState<null | 'create' | string>(null);
  const [categoryNameInput, setCategoryNameInput] = useState('');
  const [categoryActionError, setCategoryActionError] = useState<string | null>(null);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setNameError(null);
    setCategoryError(null);
    setDurationError(null);
    setSuccessMsg(null);
    setCategoryFormMode(null);
    setFormMode('create');
  };

  const openEdit = (svc: ServiceItem) => {
    setForm({
      name: svc.name,
      categoryId: svc.categoryId,
      vertical: svc.vertical ?? VERTICALS[0],
      gender: svc.gender,
      durationMinutes: svc.durationMinutes,
      bufferBeforeMinutes: svc.bufferBeforeMinutes,
      bufferAfterMinutes: svc.bufferAfterMinutes,
      price: svc.price ?? '',
      currency: svc.currency,
    });
    setNameError(null);
    setCategoryError(null);
    setDurationError(null);
    setSuccessMsg(null);
    setCategoryFormMode(null);
    setFormMode(svc.id);
  };

  const closeForm = () => {
    setFormMode(null);
    setNameError(null);
    setCategoryError(null);
    setDurationError(null);
  };

  const handleField = (field: keyof FormState, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === 'name' && nameError) setNameError(null);
    if (field === 'categoryId' && categoryError) setCategoryError(null);
    if (field === 'durationMinutes' && durationError) setDurationError(null);
  };

  const validateForm = (): boolean => {
    let valid = true;
    if (!form.name.trim()) {
      setNameError(t('services.name_required'));
      valid = false;
    }
    if (!form.categoryId) {
      setCategoryError(t('serviceCategories.required'));
      valid = false;
    }
    if (!form.durationMinutes || Number(form.durationMinutes) <= 0) {
      setDurationError(t('services.duration_required'));
      valid = false;
    }
    return valid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg(null);
    if (!validateForm()) return;

    const payload = {
      name: form.name.trim(),
      categoryId: form.categoryId,
      vertical: form.vertical as (typeof VERTICALS)[number],
      gender: form.gender as (typeof SERVICE_GENDERS)[number],
      durationMinutes: Number(form.durationMinutes),
      bufferBeforeMinutes: Number(form.bufferBeforeMinutes),
      bufferAfterMinutes: Number(form.bufferAfterMinutes),
      price: form.price.trim() === '' ? undefined : form.price.trim(),
      currency: form.currency,
    };

    if (formMode === 'create') {
      createMutation.mutate(payload, {
        onSuccess: () => {
          setSuccessMsg(t('services.success_created'));
          closeForm();
        },
      });
    } else if (formMode) {
      updateMutation.mutate(
        { id: formMode, ...payload },
        {
          onSuccess: () => {
            setSuccessMsg(t('services.success_updated'));
            closeForm();
          },
        },
      );
    }
  };

  const isMutating = createMutation.isPending || updateMutation.isPending;
  const mutationError = createMutation.error ?? updateMutation.error;

  // `?? EMPTY_SERVICES`, not `?? []`: an inline `[]` is a new identity on every
  // render while the query is pending, which would make the `useMemo` below
  // depend on something that always changes — the exact defect it exists to fix.
  const services: ServiceItem[] = listQuery.data ?? EMPTY_SERVICES;
  const categories: CategoryItem[] = categoryListQuery.data ?? [];

  // Grouped once per data change, not once per category per render. The list
  // below used to `services.filter(...)` inside `categories.map(...)`, which is
  // O(categories x services) on every keystroke of the inline edit form — and
  // `services` includes archived rows, so it is the full catalogue.
  const servicesByCategory = useMemo(() => {
    const byCategory = new Map<string, ServiceItem[]>();
    for (const svc of services) {
      const bucket = byCategory.get(svc.categoryId);
      if (bucket) bucket.push(svc);
      else byCategory.set(svc.categoryId, [svc]);
    }
    return byCategory;
  }, [services]);

  // ---- Category CRUD ----

  const openCategoryCreate = () => {
    setCategoryNameInput('');
    setCategoryActionError(null);
    setFormMode(null);
    setCategoryFormMode('create');
  };

  const openCategoryRename = (cat: CategoryItem) => {
    setCategoryNameInput(cat.name);
    setCategoryActionError(null);
    setFormMode(null);
    setCategoryFormMode(cat.id);
  };

  const closeCategoryForm = () => {
    setCategoryFormMode(null);
  };

  const categoryFormSaving = categoryCreateMutation.isPending || categoryUpdateMutation.isPending;
  const categoryMutationError = categoryCreateMutation.error ?? categoryUpdateMutation.error;

  const submitCategoryForm = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = categoryNameInput.trim();
    if (!trimmed) return;

    if (categoryFormMode === 'create') {
      categoryCreateMutation.mutate({ name: trimmed }, { onSuccess: closeCategoryForm });
    } else if (categoryFormMode) {
      categoryUpdateMutation.mutate(
        { id: categoryFormMode, name: trimmed },
        { onSuccess: closeCategoryForm },
      );
    }
  };

  const moveCategory = (categoryId: string, direction: -1 | 1) => {
    const idx = categories.findIndex((c) => c.id === categoryId);
    const swapIdx = idx + direction;
    if (idx < 0 || swapIdx < 0 || swapIdx >= categories.length) return;
    const ids = categories.map((c) => c.id);
    const current = ids[idx];
    const neighbor = ids[swapIdx];
    if (current === undefined || neighbor === undefined) return;
    ids[idx] = neighbor;
    ids[swapIdx] = current;
    setCategoryActionError(null);
    categoryReorderMutation.mutate({ ids });
  };

  const deleteCategory = (cat: CategoryItem) => {
    setCategoryActionError(null);
    categoryDeleteMutation.mutate(
      { id: cat.id },
      {
        onSuccess: () => {
          invalidateList();
        },
        onError: (err: unknown) => {
          const message = (err as { message?: string } | undefined)?.message;
          setCategoryActionError(
            message === 'category-has-services'
              ? t('serviceCategories.has_services')
              : t('common.error'),
          );
        },
      },
    );
  };

  /** Shared style for native <select> elements (no DS Select component). */
  const selectStyle: React.CSSProperties = {
    height: 'var(--control-h)',
    paddingInline: 'var(--sp-5)',
    border: '1px solid var(--border-strong)',
    borderRadius: 'var(--r-sm)',
    background: 'var(--bg-surface)',
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--fs-body-l)',
    color: 'var(--text-primary)',
    width: '100%',
  };

  const isLoading = listQuery.isPending || categoryListQuery.isPending;
  const loadError = listQuery.error ?? categoryListQuery.error;

  return (
    <div style={{ maxInlineSize: '720px', marginInline: 'auto', padding: '2rem 1rem' }}>
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBlockEnd: '1.5rem',
          flexWrap: 'wrap',
          gap: '0.5rem',
        }}
      >
        <h1 style={{ fontSize: '1.25rem', fontWeight: 600 }}>{t('services.title')}</h1>
        {canEdit && formMode === null && (
          <Button
            variant="primary"
            size="sm"
            onClick={openCreate}
            aria-label={t('services.new_service')}
            data-testid="add-service"
          >
            {t('services.new_service')}
          </Button>
        )}
      </div>

      {/* Read-only notice for non-editors */}
      {!canEdit && (
        <p
          role="status"
          style={{
            background: 'var(--warn-wash)',
            border: '1px solid var(--warn)',
            borderRadius: 'var(--r-sm)',
            padding: '0.5rem 0.75rem',
            marginBlockEnd: '1rem',
            fontSize: 'var(--fs-caption)',
          }}
        >
          {t('services.read_only_notice')}
        </p>
      )}

      {/* Success banner */}
      {successMsg && (
        <p role="status" style={{ color: 'var(--success)', marginBlockEnd: '1rem' }}>
          {successMsg}
        </p>
      )}

      {/* Category action error (delete conflict, reorder failure) */}
      {categoryActionError && (
        <p role="alert" style={{ color: 'var(--error)', marginBlockEnd: '1rem' }}>
          {categoryActionError}
        </p>
      )}

      {/* ---- Service create / edit form ---- */}
      {formMode !== null && canEdit && (
        <form
          onSubmit={handleSubmit}
          aria-label={
            formMode === 'create' ? t('services.new_service') : t('services.edit_service')
          }
          style={{ marginBlockEnd: '1.5rem' }}
        >
          <Card padding={20}>
            <h2
              style={{
                fontSize: '1rem',
                fontWeight: 600,
                marginBlockEnd: '1rem',
                color: 'var(--text-primary)',
              }}
            >
              {formMode === 'create' ? t('services.new_service') : t('services.edit_service')}
            </h2>

            {/* Name */}
            <div style={{ marginBlockEnd: '1rem' }}>
              <Input
                label={t('services.name_label')}
                value={form.name}
                onChange={(e) => handleField('name', e.target.value)}
                disabled={isMutating}
                aria-describedby={nameError ? 'svc-name-error' : undefined}
              />
              {nameError && (
                <p
                  id="svc-name-error"
                  role="alert"
                  style={{
                    color: 'var(--error)',
                    fontSize: 'var(--fs-caption)',
                    marginBlockStart: 'var(--sp-2)',
                  }}
                >
                  {nameError}
                </p>
              )}
            </div>

            {/* Category — the owner-defined grouping this page's list is organized
                by. Required: no service can be created without one. */}
            <div style={{ marginBlockEnd: '1rem' }}>
              <label
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--sp-3)',
                  fontFamily: 'var(--font-sans)',
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--fs-label)',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                  }}
                >
                  {t('serviceCategories.picker_label')}
                </span>
                <select
                  value={form.categoryId}
                  onChange={(e) => handleField('categoryId', e.target.value)}
                  disabled={isMutating}
                  data-testid="svc-category-select"
                  aria-describedby={categoryError ? 'svc-category-error' : undefined}
                  style={selectStyle}
                >
                  <option value="" />
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </label>
              {categoryError && (
                <p
                  id="svc-category-error"
                  role="alert"
                  style={{
                    color: 'var(--error)',
                    fontSize: 'var(--fs-caption)',
                    marginBlockStart: 'var(--sp-2)',
                  }}
                >
                  {categoryError}
                </p>
              )}
            </div>

            {/* Vertical — no DS Select component; keep native <select> */}
            <div style={{ marginBlockEnd: '1rem' }}>
              <label
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--sp-3)',
                  fontFamily: 'var(--font-sans)',
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--fs-label)',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                  }}
                >
                  {t('services.vertical_label')}
                </span>
                <select
                  value={form.vertical}
                  onChange={(e) => handleField('vertical', e.target.value)}
                  disabled={isMutating}
                  data-testid="svc-vertical-select"
                  style={selectStyle}
                >
                  {VERTICALS.map((v) => (
                    <option key={v} value={v}>
                      {t(`vertical.${v}`)}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* Gender — no DS Select component; keep native <select> */}
            <div style={{ marginBlockEnd: '1rem' }}>
              <label
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--sp-3)',
                  fontFamily: 'var(--font-sans)',
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--fs-label)',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                  }}
                >
                  {t('services.gender_label')}
                </span>
                <select
                  value={form.gender}
                  onChange={(e) => handleField('gender', e.target.value)}
                  disabled={isMutating}
                  style={selectStyle}
                >
                  {SERVICE_GENDERS.map((g) => (
                    <option key={g} value={g}>
                      {t(`gender.${g}`)}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* Duration */}
            <div style={{ marginBlockEnd: '1rem' }}>
              <Input
                label={t('services.duration_label')}
                type="number"
                value={String(form.durationMinutes)}
                onChange={(e) => handleField('durationMinutes', Number(e.target.value))}
                disabled={isMutating}
                aria-describedby={durationError ? 'svc-duration-error' : undefined}
                min={1}
              />
              {durationError && (
                <p
                  id="svc-duration-error"
                  role="alert"
                  style={{
                    color: 'var(--error)',
                    fontSize: 'var(--fs-caption)',
                    marginBlockStart: 'var(--sp-2)',
                  }}
                >
                  {durationError}
                </p>
              )}
            </div>

            {/* Buffer before */}
            <div style={{ marginBlockEnd: '1rem' }}>
              <Input
                label={t('services.buffer_before_label')}
                type="number"
                value={String(form.bufferBeforeMinutes)}
                onChange={(e) => handleField('bufferBeforeMinutes', Number(e.target.value))}
                disabled={isMutating}
                min={0}
              />
            </div>

            {/* Buffer after */}
            <div style={{ marginBlockEnd: '1rem' }}>
              <Input
                label={t('services.buffer_after_label')}
                type="number"
                value={String(form.bufferAfterMinutes)}
                onChange={(e) => handleField('bufferAfterMinutes', Number(e.target.value))}
                disabled={isMutating}
                min={0}
              />
            </div>

            {/* Price */}
            <div style={{ marginBlockEnd: '1rem' }}>
              <Input
                label={t('services.price_label')}
                type="number"
                value={form.price}
                onChange={(e) => handleField('price', e.target.value)}
                disabled={isMutating}
                min={0}
                step="0.01"
              />
            </div>

            {/* Mutation error */}
            {mutationError && (
              <p role="alert" style={{ color: 'var(--error)', marginBlockEnd: '0.75rem' }}>
                {mutationError.message}
              </p>
            )}

            {/* Form actions */}
            <div
              style={{
                display: 'flex',
                gap: 'var(--sp-3)',
                flexWrap: 'wrap',
                marginBlockStart: 'var(--sp-4)',
              }}
            >
              <Button type="submit" variant="primary" size="sm" disabled={isMutating}>
                {isMutating ? t('services.saving') : t('services.save')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={closeForm}
                disabled={isMutating}
              >
                {t('services.cancel')}
              </Button>
            </div>
          </Card>
        </form>
      )}

      {/* ---- Category create / rename form ---- */}
      {categoryFormMode !== null && canEdit && (
        <form
          onSubmit={submitCategoryForm}
          aria-label={
            categoryFormMode === 'create'
              ? t('serviceCategories.add')
              : t('serviceCategories.rename')
          }
          style={{ marginBlockEnd: '1.5rem' }}
        >
          <Card padding={20}>
            <h2
              style={{
                fontSize: '1rem',
                fontWeight: 600,
                marginBlockEnd: '1rem',
                color: 'var(--text-primary)',
              }}
            >
              {categoryFormMode === 'create'
                ? t('serviceCategories.add')
                : t('serviceCategories.rename')}
            </h2>

            <div style={{ marginBlockEnd: '1rem' }}>
              <Input
                label={t('serviceCategories.name_label')}
                placeholder={t('serviceCategories.name_placeholder')}
                value={categoryNameInput}
                onChange={(e) => setCategoryNameInput(e.target.value)}
                disabled={categoryFormSaving}
              />
            </div>

            {categoryMutationError && (
              <p role="alert" style={{ color: 'var(--error)', marginBlockEnd: '0.75rem' }}>
                {categoryMutationError.message}
              </p>
            )}

            <div
              style={{
                display: 'flex',
                gap: 'var(--sp-3)',
                flexWrap: 'wrap',
                marginBlockStart: 'var(--sp-4)',
              }}
            >
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={categoryFormSaving || !categoryNameInput.trim()}
              >
                {categoryFormSaving ? t('services.saving') : t('services.save')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={closeCategoryForm}
                disabled={categoryFormSaving}
              >
                {t('services.cancel')}
              </Button>
            </div>
          </Card>
        </form>
      )}

      {/* ---- Add category entry point — above the grouped list ---- */}
      {canEdit && categoryFormMode === null && (
        <div style={{ marginBlockEnd: '1rem' }}>
          <Button
            variant="secondary"
            size="sm"
            onClick={openCategoryCreate}
            data-testid="add-category"
          >
            {t('serviceCategories.add')}
          </Button>
        </div>
      )}

      {/* ---- Services list, grouped by category ---- */}
      {isLoading && <p>{t('common.loading')}</p>}

      {loadError && (
        <p role="alert" style={{ color: 'var(--error)' }}>
          {t('common.error')}: {loadError.message}
        </p>
      )}

      {listQuery.data && services.length === 0 && (
        <p style={{ color: 'var(--text-tertiary)' }}>{t('services.no_services')}</p>
      )}

      {listQuery.data &&
        services.length > 0 &&
        categoryListQuery.data &&
        categories.length === 0 && (
          <p style={{ color: 'var(--text-tertiary)' }}>{t('serviceCategories.empty')}</p>
        )}

      {listQuery.data && services.length > 0 && categories.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-6)' }}>
          {categories.map((cat, idx) => {
            const catServices = servicesByCategory.get(cat.id) ?? EMPTY_SERVICES;
            const isFirst = idx === 0;
            const isLast = idx === categories.length - 1;
            return (
              <div key={cat.id}>
                {/* Category header — one level below the page's h1 */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBlockEnd: '0.75rem',
                    flexWrap: 'wrap',
                    gap: '0.5rem',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)' }}>
                    <h2
                      style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--text-primary)' }}
                    >
                      {cat.name}
                    </h2>
                    <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-tertiary)' }}>
                      {cat.serviceCount} {t('serviceCategories.service_count')}
                    </span>
                  </div>
                  {canEdit && (
                    <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={isFirst}
                        onClick={() => moveCategory(cat.id, -1)}
                        aria-label={`${t('serviceCategories.move_up')} ${cat.name}`}
                        data-testid={`category-up-${cat.id}`}
                      >
                        {t('serviceCategories.move_up')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={isLast}
                        onClick={() => moveCategory(cat.id, 1)}
                        aria-label={`${t('serviceCategories.move_down')} ${cat.name}`}
                        data-testid={`category-down-${cat.id}`}
                      >
                        {t('serviceCategories.move_down')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openCategoryRename(cat)}
                        aria-label={`${t('serviceCategories.rename')} ${cat.name}`}
                        data-testid={`category-rename-${cat.id}`}
                      >
                        {t('serviceCategories.rename')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => deleteCategory(cat)}
                        aria-label={`${t('serviceCategories.delete')} ${cat.name}`}
                        data-testid={`category-delete-${cat.id}`}
                      >
                        {t('serviceCategories.delete')}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Services within this category */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
                  {catServices.map((svc) => (
                    <Card
                      key={svc.id}
                      data-testid="service-row"
                      padding={0}
                      style={{ opacity: svc.isActive ? 1 : 0.5 }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--sp-4)',
                          padding: '12px 16px',
                          flexWrap: 'wrap',
                        }}
                      >
                        {/* Service info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: 'var(--fs-body-l)',
                              color: 'var(--text-primary)',
                            }}
                          >
                            {svc.name}
                          </div>
                          <div
                            style={{
                              fontSize: 'var(--fs-caption)',
                              color: 'var(--text-secondary)',
                              marginTop: 2,
                            }}
                          >
                            {svc.durationMinutes} דק׳ · {t(`gender.${svc.gender}`)}
                          </div>
                        </div>

                        {/* Price */}
                        {svc.price != null && (
                          <span
                            style={{
                              fontSize: 'var(--fs-label)',
                              fontWeight: 600,
                              color: 'var(--text-primary)',
                            }}
                          >
                            <bdi>
                              {svc.price} {svc.currency}
                            </bdi>
                          </span>
                        )}

                        {/* Status badge */}
                        <Badge tone={svc.isActive ? 'success' : 'neutral'}>
                          {svc.isActive ? t('services.active_badge') : t('services.archived_badge')}
                        </Badge>

                        {/* Action buttons */}
                        {canEdit && (
                          <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => openEdit(svc)}
                              aria-label={`${t('services.edit_service')} ${svc.name}`}
                            >
                              {t('services.edit_service')}
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() =>
                                setActiveMutation.mutate({ id: svc.id, isActive: !svc.isActive })
                              }
                              aria-label={
                                svc.isActive
                                  ? `${t('services.archive')} ${svc.name}`
                                  : `${t('services.unarchive')} ${svc.name}`
                              }
                            >
                              {svc.isActive ? t('services.archive') : t('services.unarchive')}
                            </Button>
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
